import { Box, Text } from '@chakra-ui/react';

import {
  type CompositionV2Entity,
  ENTITY_V2_QUERY,
  EntityV2Type,
  PositionsEntityKind,
} from '@revelio/data-access';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, FilterType } from '../../filters/types/filters';
import { useSafeChart } from '../Chart/ChartProvider';
import { PlotFilterType } from '../PageConfigProvider/types/item';
import { TOP_N_LIMIT } from './constants';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

type CompetitorDataProviderProps = {
  entityId?: number;
  children: DataProviderChildren<CompositionV2Entity[] | undefined>;
};

export const CompetitorDataProvider = ({
  entityId,
  children,
}: CompetitorDataProviderProps) => {
  const effectiveFilters = useEffectiveFilters();

  const selectedCompanyId = (() => {
    if (entityId) return entityId;
    const companyFilter = effectiveFilters.find(
      (filter) => filter.id === FilterId.COMPANY
    );
    if (!companyFilter || companyFilter.type !== FilterType.TREE) return null;

    const companyId = Number(
      companyFilter.selections?.[0]?.id?.split('_')?.pop()
    );
    // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
    if (isNaN(companyId)) return null;
    return companyId;
  })();

  if (!selectedCompanyId) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Text textAlign="center">No Selected Company Found</Text>
      </Box>
    );
  }

  return (
    <CompetitorQuery selectedCompanyId={selectedCompanyId}>
      {children}
    </CompetitorQuery>
  );
};

const CompetitorQuery = ({
  selectedCompanyId,
  children,
}: {
  selectedCompanyId: number;
  children: DataProviderChildren<CompositionV2Entity[] | undefined>;
}) => {
  const { item } = useSafeChart();
  const topNFilterValue = item?.plot?.plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N
  )?.value;
  const entityLimit =
    typeof topNFilterValue === 'number' &&
    Number.isFinite(topNFilterValue) &&
    topNFilterValue > 0
      ? topNFilterValue
      : TOP_N_LIMIT;

  const numCompetitors = Math.max(0, entityLimit - 1);

  const [{ data: entityData, status }] = useUrqlQueryWithStatus({
    query: ENTITY_V2_QUERY,
    variables: {
      req: {
        entID: selectedCompanyId,
        entType: EntityV2Type.Company,
        numCompetitors,
      },
    },
  });

  const entities = (() => {
    if (status === DataProviderQueryStatus.Fetching) return undefined;

    const competitors = entityData?.entityV2?.competitors || [];
    const competitorIds = competitors
      .map((competitor) => competitor?.id)
      .filter((id): id is number => id !== null && id !== undefined);

    const allIds = [selectedCompanyId, ...competitorIds]
      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
      .filter((id) => !isNaN(Number(id)))
      .slice(0, entityLimit);

    return allIds.map((id) => ({
      EntID: Number(id),
      EntType: PositionsEntityKind.Company,
    }));
  })();

  return children({ data: entities, status });
};
